/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { useEffect, useMemo, useState } from 'react';

import { marked } from 'marked';

import { loggedUserInfoStore } from '@/stores';

interface LoginPopup {
  content: string;
  imageUrl: string;
  imageRatio: string;
}

const styles = `
.login-popup-overlay{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.45);padding:16px}
.login-popup-modal{width:min(560px,100%);max-height:calc(100vh - 32px);overflow:hidden;background:#fff;border-radius:12px;box-shadow:0 18px 48px rgba(15,23,42,.22);color:#1f2329}
.login-popup-header{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:20px 24px;border-bottom:1px solid #e5e6eb}
.login-popup-title{display:flex;align-items:center;gap:8px;font-size:16px;font-weight:600;line-height:24px}
.login-popup-close{border:0;background:transparent;color:#4e5969;font-size:24px;line-height:1;cursor:pointer}
.login-popup-body{padding:24px}
.login-popup-image{display:flex;width:100%;max-height:52vh;overflow:hidden;border-radius:8px;background:#f7f8fa;margin-bottom:16px}
.login-popup-image[data-ratio="auto"]{display:block;max-height:none;background:transparent}
.login-popup-image img{display:block;width:100%;height:100%;object-fit:contain}
.login-popup-image[data-ratio="auto"] img{height:auto;max-height:260px;border-radius:8px;background:#f7f8fa}
.login-popup-content{max-height:45vh;overflow-y:auto;padding-right:8px;line-height:1.7}
.login-popup-content p{margin:0 0 12px}
.login-popup-content p:last-child{margin-bottom:0}
.login-popup-footer{display:flex;justify-content:flex-end;padding:16px 24px;border-top:1px solid #e5e6eb}
.login-popup-actions{display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap}
.login-popup-secondary{border:1px solid #dcdfe6;border-radius:6px;background:#fff;color:#1f2329;font-size:14px;font-weight:500;line-height:20px;padding:8px 16px;cursor:pointer}
.login-popup-confirm{border:0;border-radius:6px;background:#165dff;color:#fff;font-size:14px;font-weight:500;line-height:20px;padding:8px 16px;cursor:pointer}
@media (max-width:640px){.login-popup-overlay{align-items:flex-end}.login-popup-modal{width:100%;border-radius:12px 12px 0 0}.login-popup-body{padding:20px}}
`;

const imageRatioStyleMap: Record<string, string> = {
  '1/1': '1 / 1',
  '2/1': '2 / 1',
  '4/3': '4 / 3',
  '16/9': '16 / 9',
};

function normalizeImageRatio(value: unknown) {
  return typeof value === 'string' && imageRatioStyleMap[value]
    ? value
    : 'auto';
}

function hashString(input: string) {
  let hash = 0;
  if (!input) {
    return '0';
  }

  for (let i = 0; i < input.length; i += 1) {
    const chr = input.charCodeAt(i);
    // Match the NewAPI console popup storage key.
    // eslint-disable-next-line no-bitwise
    hash = (hash << 5) - hash + chr;
    // eslint-disable-next-line no-bitwise
    hash |= 0;
  }

  return hash.toString(36);
}

function getNewApiUserId() {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    const rawUser = window.localStorage.getItem('user');
    if (rawUser) {
      const user = JSON.parse(rawUser);
      if (user?.id !== undefined && user?.id !== null) {
        return String(user.id);
      }
      if (user?.username) {
        return String(user.username);
      }
    }

    return window.localStorage.getItem('uid') || '';
  } catch {
    return window.localStorage.getItem('uid') || '';
  }
}

function getTodayKey() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function isReminderDismissed(storageKey: string) {
  try {
    if (window.localStorage.getItem(`${storageKey}:never`) === 'true') {
      return true;
    }
    if (window.localStorage.getItem(`${storageKey}:today`) === getTodayKey()) {
      return true;
    }
    if (window.sessionStorage.getItem(storageKey) === 'closed') {
      return true;
    }
  } catch {
    // Session or local storage can be disabled; showing the notice is valid.
  }

  return false;
}

function rememberDismiss(storageKey: string, mode = 'session') {
  try {
    if (mode === 'never') {
      window.localStorage.setItem(`${storageKey}:never`, 'true');
      return;
    }
    if (mode === 'today') {
      window.localStorage.setItem(`${storageKey}:today`, getTodayKey());
      return;
    }
    window.sessionStorage.setItem(storageKey, 'closed');
  } catch {
    // Ignore storage failures.
  }
}

function normalizeLoginPopup(value: unknown): LoginPopup {
  let popupValue = value;
  if (typeof popupValue === 'string') {
    try {
      popupValue = JSON.parse(popupValue);
    } catch {
      popupValue = {};
    }
  }

  if (!popupValue || typeof popupValue !== 'object') {
    return { content: '', imageUrl: '', imageRatio: 'auto' };
  }

  const raw = popupValue as Record<string, unknown>;
  const content = typeof raw.content === 'string' ? raw.content.trim() : '';
  const imageUrl =
    typeof raw.image_url === 'string'
      ? raw.image_url.trim()
      : typeof raw.imageUrl === 'string'
        ? raw.imageUrl.trim()
        : '';
  const imageRatio = normalizeImageRatio(raw.image_ratio ?? raw.imageRatio);

  return { content, imageUrl, imageRatio };
}

const LoginPopupNotice = () => {
  const loggedUser = loggedUserInfoStore((state) => state.user);
  const [visible, setVisible] = useState(false);
  const [popup, setPopup] = useState<LoginPopup>({
    content: '',
    imageUrl: '',
    imageRatio: 'auto',
  });
  const [storageKey, setStorageKey] = useState('');

  const userId = useMemo(
    () => getNewApiUserId() || loggedUser.username || '',
    [loggedUser.username],
  );

  useEffect(() => {
    let cancelled = false;

    if (!userId) {
      setVisible(false);
      return () => {
        cancelled = true;
      };
    }

    fetch('/api/status', {
      credentials: 'include',
      cache: 'no-store',
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled) {
          return;
        }

        const status = payload?.data || {};
        const enabled =
          status.login_popup_enabled === true ||
          status.login_popup_enabled === 'true';
        const nextPopup = normalizeLoginPopup(status.login_popup);

        if (!enabled || (!nextPopup.content && !nextPopup.imageUrl)) {
          setVisible(false);
          return;
        }

        const nextStorageKey = `login-popup:${userId}:${hashString(
          JSON.stringify(nextPopup),
        )}`;

        if (isReminderDismissed(nextStorageKey)) {
          setVisible(false);
          return;
        }

        setPopup(nextPopup);
        setStorageKey(nextStorageKey);
        setVisible(true);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const contentHtml = useMemo(() => {
    if (!popup.content) {
      return '';
    }
    return marked.parse(popup.content);
  }, [popup.content]);

  const close = (mode = 'session') => {
    setVisible(false);
    if (storageKey) {
      rememberDismiss(storageKey, mode);
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <>
      <style>{styles}</style>
      <div
        className="login-popup-overlay"
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            close();
          }
        }}>
        <section
          className="login-popup-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-popup-title">
          <header className="login-popup-header">
            <div className="login-popup-title" id="login-popup-title">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true">
                <path d="m3 11 18-5v12L3 14v-3z" />
                <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
              </svg>
              <span>登录公告</span>
            </div>
            <button
              className="login-popup-close"
              type="button"
              aria-label="关闭"
              onClick={() => close('session')}>
              ×
            </button>
          </header>

          <div className="login-popup-body">
            {popup.imageUrl ? (
              <div
                className="login-popup-image"
                data-ratio={popup.imageRatio}
                style={
                  imageRatioStyleMap[popup.imageRatio]
                    ? { aspectRatio: imageRatioStyleMap[popup.imageRatio] }
                    : undefined
                }>
                <img src={popup.imageUrl} alt="登录弹窗图片" />
              </div>
            ) : null}

            {contentHtml ? (
              <div
                className="login-popup-content"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            ) : null}
          </div>

          <footer className="login-popup-footer">
            <div className="login-popup-actions">
              <button
                className="login-popup-secondary"
                type="button"
                onClick={() => close('today')}>
                {'\u4eca\u65e5\u5df2\u8bfb'}
              </button>
              <button
                className="login-popup-confirm"
                type="button"
                onClick={() => close('never')}>
                {'\u6c38\u4e45\u5173\u95ed'}
              </button>
            </div>
          </footer>
        </section>
      </div>
    </>
  );
};

export default LoginPopupNotice;
